import React, { useEffect, useState, Suspense, lazy } from "react";
import { HelmetProvider } from 'react-helmet-async';
import { Routes, Route, Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import AuthService from "./service/auth.service";
import {
  isInstitutionOnlyAdmin,
  isInstitutionHomeLearner,
  isPathAllowedForInstitutionOnlyUser,
  isPathAllowedForInstitutionHomeLearner,
} from "./utils/authRedirects";
import ReferralService from "./service/referral.service";
import LiveExamService from "./service/liveExam.service";
import EventBus from "./components/common/EventBus";
import { validateSession, clearSessionAndRedirect, shouldRefreshAccessToken } from "./utils/tokenValidator";
import { initAnalytics, trackPageView } from "./utils/analytics";
import { EXAM_PREP_LABEL, EXAM_PREP_PATH } from "./constants/examPrep";
import {trackMockFunnel} from "./utils/mockFunnel";
import "./App.css";
import styled, { keyframes } from "styled-components";
// import {
//   BannerContext,
// } from "./components/common/BannerContext"; // Unused
import { FeatureFlagRoute } from "./components/common/FeatureFlagRoute";
import { useFeatureFlags } from "./context/FeatureFlagContext";
import { useTenantEntitlements, useBrandName } from "./context/TenantEntitlementsContext";
import { EXAM_LANDING_SLUGS } from "./data/examLandingData";
import { Flame, Keyboard, PenTool, BookOpen, FileText, Target, CreditCard, Headphones, Gamepad2, Sword, Flag, MoreHorizontal, Briefcase, MessageSquare, User, BarChart2, Gift, Users, Shield, Building, Send, Megaphone, TrendingUp, Settings, Trophy, Activity, Ticket, Mail, Link as LinkIcon, Key, Video, Rocket, Bot, Crown, Terminal, Youtube, AlertTriangle, Lock, LogOut, Layers } from 'lucide-react';


// Critical components - load immediately
import HomeComponent from "./components/course/HomeComponent";

// Lazy load non-critical components for better performance
const LearnPage = lazy(() => import("./components/learn/LearnPage"));
const LessonPlayer = lazy(() => import("./components/learn/LessonPlayer"));
const SocialDrawer = lazy(() => import("./components/common/SocialDrawer"));
const CookieConsent = lazy(() => import("./components/common/CookieConsent"));
const TypoChatbot = lazy(() => import("./components/common/TypoChatbot"));
const FeedbackForm = lazy(() => import("./components/common/FeedbackForm"));
const TelegramJoinModal = lazy(() => import("./components/common/TelegramJoinModal"));
const CoursesPage = lazy(() => import("./components/course/CoursesPage"));
const TypingDisplay = lazy(() => import("./components/common/TypingDisplay"));
const TypingDisplayTest = lazy(() => import("./components/common/TypingDisplayTests"));
const Login = lazy(() => import("./components/user/login.component"));
const Register = lazy(() => import("./components/user/register.component"));
const FeatureVideos = lazy(() => import("./components/common/FeatureVideos"));
const PreparationStart = lazy(() => import("./components/common/PreparationStart"));
const AddTutorial = lazy(() => import("./components/course/add-tutorial.component"));
// const Tutorial = lazy(() => import("./components/course/tutorial.component")); // Unused
const AllTypingCourses = lazy(() => import("./components/course/AllTypingCourses"));
const CourseDetails = lazy(() => import("./components/course/CourseDetails"));
const Profile = lazy(() => import("./components/user/profile.component"));
const BoardAdmin = lazy(() => import("./components/user/board-admin.component"));
const BoardModerator = lazy(() => import("./components/user/board-moderator.component"));
const ChooseTest = lazy(() => import("./components/course/ChooseTest"));
const PaymentComponent = lazy(() => import("./components/course/PaymentComponent"));
const ReferAndEarnComponent = lazy(() => import("./components/course/ReferAndEarnComponent"));
const ForgotPassword = lazy(() => import("./components/user/ForgotPasswordComponent"));
const SSOCallback = lazy(() => import("./components/user/SSOCallback"));
const ResetPassword = lazy(() => import("./components/user/ResetPasswordComponent"));
const VerifyOtp = lazy(() => import("./components/user/VerifyOtpComponent"));
const TestResults = lazy(() => import("./components/user/userResult.component"));
const TestDetails = lazy(() => import("./components/user/testDetails.component"));
const Checkout = lazy(() => import("./components/course/CheckoutComponent"));
const About = lazy(() => import("./components/common/About"));
const { PrivacyPolicyPage, TermsOfServicePage, RefundPolicyPage, CookiePolicyPage, FAQPage, HelpCenterPage } = require("./components/common/LegalPages");
const ContactUs = lazy(() => import("./components/common/ContactUs"));
const SocialPage = lazy(() => import("./components/common/SocialPage"));
// const Leaderboard = lazy(() => import("./components/common/Leaderboard")); // Unused - using LeaderboardPage instead
const DSAProblemDocumenter = lazy(() => import("./components/course/DSAProblemComp"));
const BlogHub = lazy(() => import("./components/blog/BlogHub"));
const ArticlePage = lazy(() => import("./components/blog/ArticlePage"));
const BlogAdminPanel = lazy(() => import("./components/blog/BlogAdminPanel"));
const FeedbackAdminPanel = lazy(() => import("./components/common/FeedbackAdminPanel"));
const CouponAdminPanel = lazy(() => import("./components/common/CouponAdminPanel"));
const UnifiedCourseAdminPanel = lazy(() => import("./components/common/UnifiedCourseAdminPanel"));
const NewsletterAdminPanel = lazy(() => import("./components/common/NewsletterAdminPanel"));
const UserAdminPanel = lazy(() => import("./components/common/UserAdminPanel"));
const RateLimitAdminPanel = lazy(() => import("./components/common/RateLimitAdminPanel"));
const ApiConfigAdminPanel = lazy(() => import("./components/common/ApiConfigAdminPanel"));
const GeminiApiKeysAdminPanel = lazy(() => import("./components/admin/GeminiApiKeysAdminPanel"));
const FeatureFlagAdminPanel = lazy(() => import("./components/common/FeatureFlagAdminPanel"));
const CommunityConfigAdminPanel = lazy(() => import("./components/common/CommunityConfigAdminPanel"));
const StudyRoomAdminPanel = lazy(() => import("./components/common/StudyRoomAdminPanel"));
const BulkEmailAdminPanel = lazy(() => import("./components/common/BulkEmailAdminPanel"));
const WhatsAppSender = lazy(() => import("./components/common/WhatsAppSender"));
const EmailQueueDashboard = lazy(() => import("./components/common/EmailQueueDashboard"));
const TelegramMarketingPanel = lazy(() => import("./components/common/TelegramMarketingPanel"));
const BlogSeoAdminPanel = lazy(() => import("./components/common/BlogSeoAdminPanel"));
const AutomationCenterAdminPanel = lazy(() => import("./components/common/AutomationCenterAdminPanel"));
const OracleSshAdminPanel = lazy(() => import("./components/common/OracleSshAdminPanel"));
const ExamLandingPage = lazy(() => import("./components/seo/ExamLandingPage"));
const ExamGuidesHub = lazy(() => import("./components/seo/ExamGuidesHub"));
const AllStateTypingTestsPage = lazy(() => import("./components/seo/AllStateTypingTestsPage"));
const SubscriptionWarrantyAdminPanel = lazy(() => import("./components/common/SubscriptionWarrantyAdminPanel"));
const StudySubscriptionAdminPanel = lazy(() => import("./components/common/StudySubscriptionAdminPanel"));
const CorruptedTestResultsAdminPanel = lazy(() => import("./components/common/CorruptedTestResultsAdminPanel"));
const AnalyticsDashboard = lazy(() => import("./components/common/AnalyticsDashboard"));
const ChangePasswordPanel = lazy(() => import("./components/common/ChangePasswordPanel"));
const NotFoundPage = lazy(() => import("./components/common/NotFoundPage"));
const DailyChallenge = lazy(() => import("./components/features/DailyChallenge/DailyChallenge"));
const Achievements = lazy(() => import("./components/features/Achievements/Achievements"));
const TypingRace = lazy(() => import("./components/features/Race/TypingRace"));
const GamesHub = lazy(() => import("./components/features/Games/GamesHub"));
const WordBlaster = lazy(() => import("./components/features/Games/WordBlaster"));
const TypingNinja = lazy(() => import("./components/features/Games/TypingNinja"));
const CodeRunner = lazy(() => import("./components/features/Games/CodeRunner"));
const WordTetris = lazy(() => import("./components/features/Games/WordTetris"));
const ZombieDefense = lazy(() => import("./components/features/Games/ZombieDefense"));
const SpaceInvaders = lazy(() => import("./components/features/Games/SpaceInvaders"));
const Tournaments = lazy(() => import("./components/features/Tournaments/Tournaments"));
const LiveExams = lazy(() => import("./components/features/LiveExams/LiveExams"));
const LiveExamLeaderboard = lazy(() => import("./components/features/LiveExams/LiveExamLeaderboard"));
const LeaderboardPage = lazy(() => import("./components/features/Leaderboard/Leaderboard"));
const SocialTasksPage = lazy(() => import("./components/features/SocialTasks/SocialTasksPage"));
const TournamentAdmin = lazy(() => import("./components/features/Admin/TournamentAdmin"));
const LiveExamAdmin = lazy(() => import("./components/features/Admin/LiveExamAdmin"));
const SocialHub = lazy(() => import("./components/features/Social/SocialHub"));
const CommunityHub = lazy(() => import("./components/community/CommunityHub"));
const MentalHealthSupport = lazy(() => import("./components/community/MentalHealthSupport"));
const StudyRoom = lazy(() => import("./components/community/StudyRoom"));
const StudyAnalyticsDashboard = lazy(() => import("./components/community/StudyAnalyticsDashboard"));
const StudyBuddy = lazy(() => import("./components/community/StudyBuddy"));
const DiscussionGroups = lazy(() => import("./components/community/DiscussionGroups"));
const ForestGroup = lazy(() => import("./components/community/ForestGroup"));
const StudyGroups = lazy(() => import("./components/community/StudyGroups"));

// Junior and Institutional Components
const AdventureMap = lazy(() => import("./components/junior/AdventureMap"));
const WorldView = lazy(() => import("./components/junior/WorldView"));
const ParentPortal = lazy(() => import("./components/junior/ParentPortal"));
const DigitalCitizenship = lazy(() => import("./components/junior/DigitalCitizenship"));
const Themes = lazy(() => import("./components/junior/Themes"));
const Games = lazy(() => import("./components/junior/Games"));
const InstitutionalDashboard = lazy(() => import("./components/institutional/InstitutionalDashboard"));
const InstitutionStudentHome = lazy(() => import("./components/institutional/InstitutionStudentHome"));
const SuperAdminDashboard = lazy(() => import("./components/admin/SuperAdminDashboard"));
const YouTubeStudioPanel = lazy(() => import("./components/admin/YouTubeStudioPanel"));
const MarketingConfigPanel = lazy(() => import("./components/admin/MarketingConfigPanel"));
const YogaMeditation = lazy(() => import("./components/community/YogaMeditation"));

// Study Schedule
const StudyDashboard = lazy(() => import("./components/features/Study/StudyDashboard"));
const MockTestArena = lazy(() => import("./components/features/Study/MockTestArena"));
const TestAnalyticsDashboard = lazy(() => import("./components/features/Study/TestAnalyticsDashboard"));
const StudentMockTestList = lazy(() => import("./components/features/Study/StudentMockTestList"));
const StudyLibraryHub = lazy(() => import("./components/features/Study/StudyLibraryHub"));
const VideoHub = lazy(() => import("./components/features/Study/VideoHub"));
const TestPassStore = lazy(() => import("./components/features/Study/TestPassStore"));
const SystemDesignPage = lazy(() => import("./components/features/SystemDesign/SystemDesignPage"));
const SystemDesignShareView = lazy(() => import("./components/features/SystemDesign/SystemDesignShareView"));
const PublicLandingPage = lazy(() => import("./components/marketing/PublicLandingPage"));

// Rank Predictor
const RankPredictorExamList = lazy(() => import("./components/features/RankPredictor/ExamList"));
const RankPredictorCalculator = lazy(() => import("./components/features/RankPredictor/ExamCalculator"));
const ResultAnalyzer = lazy(() => import("./components/features/RankPredictor/ResultAnalyzer"));
const SarkariResultsHub = lazy(() => import("./components/features/SarkariResults/SarkariResultsHub"));
const SarkariResultsDetail = lazy(() => import("./components/features/SarkariResults/SarkariResultsDetail"));
const StenoHome = lazy(() => import("./components/steno/StenoHome"));
const StenoPassages = lazy(() => import("./components/steno/StenoPassages"));
const StenoAdminPanel = lazy(() => import("./components/steno/StenoAdminPanel"));
const StenoPractice = lazy(() => import("./components/steno/StenoPractice"));
const StenoResultPage = lazy(() => import("./components/steno/StenoResultPage"));
const StenoResults = lazy(() => import("./components/steno/StenoResults"));
const StenoLearningHub = lazy(() => import("./components/steno/StenoLearningHub"));
const StenoModulePage = lazy(() => import("./components/steno/StenoModulePage"));
const StenoLessonViewer = lazy(() => import("./components/steno/StenoLessonViewer"));
const StenoPricing = lazy(() => import("./components/steno/StenoPricing"));
const TypingTestPricing = lazy(() => import("./components/course/TypingTestPricing"));
const TypingTestPlanAdminPanel = lazy(() => import("./components/common/TypingTestPlanAdminPanel"));
const ExamMockAdminPanel = lazy(() => import("./components/common/ExamMockAdminPanel"));
const MockPrintView = lazy(() => import("./components/features/Study/MockPrintView"));
// PDF Tools
const PdfToolsHub = lazy(() => import("./components/features/PdfTools/PdfToolsHub"));
const MergePdf = lazy(() => import("./components/features/PdfTools/tools/MergePdf"));
const JpgToPdf = lazy(() => import("./components/features/PdfTools/tools/JpgToPdf"));
const SplitPdf = lazy(() => import("./components/features/PdfTools/tools/SplitPdf"));
const ExtractPages = lazy(() => import("./components/features/PdfTools/tools/ExtractPages"));
const RemovePages = lazy(() => import("./components/features/PdfTools/tools/RemovePages"));
const RotatePdf = lazy(() => import("./components/features/PdfTools/tools/RotatePdf"));
const InvertPdf = lazy(() => import("./components/features/PdfTools/tools/InvertPdf"));
const AiSummarizer = lazy(() => import("./components/features/PdfTools/tools/AiSummarizer"));
const TranslatePdf = lazy(() => import("./components/features/PdfTools/tools/TranslatePdf"));
const PdfToMarkdown = lazy(() => import("./components/features/PdfTools/tools/PdfToMarkdown"));
const AddWatermark = lazy(() => import("./components/features/PdfTools/tools/AddWatermark"));
const AddPageNumbers = lazy(() => import("./components/features/PdfTools/tools/AddPageNumbers"));
const PdfToJpg = lazy(() => import("./components/features/PdfTools/tools/PdfToJpg"));
const CropPdf = lazy(() => import("./components/features/PdfTools/tools/CropPdf"));
const ProtectPdf = lazy(() => import("./components/features/PdfTools/tools/ProtectPdf"));
const UnlockPdf = lazy(() => import("./components/features/PdfTools/tools/UnlockPdf"));
const PdfToWord = lazy(() => import("./components/features/PdfTools/tools/PdfToWord"));
const CompressPdf = lazy(() => import("./components/features/PdfTools/tools/CompressPdf"));
const ComparePdf = lazy(() => import("./components/features/PdfTools/tools/ComparePdf"));
const ScanToPdf = lazy(() => import("./components/features/PdfTools/tools/ScanToPdf"));
const ToolPlaceholder = lazy(() => import("./components/features/PdfTools/tools/ToolPlaceholder"));
const ForInstitutes = lazy(() => import("./components/marketing/ForInstitutes"));
const GlobalExamBanner = lazy(() => import("./components/common/GlobalExamBanner"));
const SocialProofToast = lazy(() => import("./components/common/SocialProofToast"));

// Loading fallback component
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: '#050507',
    color: '#a855f7'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '50px',
        height: '50px',
        border: '4px solid rgba(168, 85, 247, 0.3)',
        borderTop: '4px solid #a855f7',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 1rem'
      }}></div>
      <p>Loading...</p>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  </div>
);

// Animations
const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(-14px) scale(0.98); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

const slideDown = keyframes`
  0% { opacity: 0; transform: translateY(-20px); }
  100% { opacity: 1; transform: translateY(0); }
`;


// Styled Components for Navbar
const Navbar = styled.nav`
  padding: 0.42rem 1.25rem;
  background: ${({ $scrolled }) =>
    $scrolled
      ? 'rgba(18, 18, 32, 0.72)'
      : 'rgba(18, 18, 32, 0.35)'};
  backdrop-filter: blur(40px) saturate(200%);
  -webkit-backdrop-filter: blur(40px) saturate(200%);
  border: 1px solid ${({ $scrolled }) =>
    $scrolled
      ? 'rgba(255, 255, 255, 0.12)'
      : 'rgba(255, 255, 255, 0.08)'};
  border-radius: 14px;
  box-shadow: ${({ $scrolled }) =>
    $scrolled
      ? `0 8px 32px rgba(0, 0, 0, 0.4),
         0 1.5px 0 0 rgba(255, 255, 255, 0.04) inset,
         0 0 0 0.5px rgba(255, 255, 255, 0.06)`
      : `0 4px 20px rgba(0, 0, 0, 0.2),
         0 1.5px 0 0 rgba(255, 255, 255, 0.06) inset,
         0 0 0 0.5px rgba(255, 255, 255, 0.04)`};
  animation: ${fadeIn} 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: fixed;
  top: ${({ $hasBanner }) => ($hasBanner ? '48px' : '8px')};
  left: 0.85rem;
  right: 0.85rem;
  z-index: 1000;
  transition: all 0.45s cubic-bezier(0.23, 1, 0.32, 1);
  gap: 0.65rem;
  overflow: visible;
  flex-wrap: nowrap;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 50%;
    border-radius: 14px 14px 0 0;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.04) 0%,
      transparent 100%
    );
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: -1px;
    left: 20%;
    right: 20%;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.12),
      transparent
    );
    pointer-events: none;
    opacity: ${({ $scrolled }) => $scrolled ? 0.6 : 1};
    transition: opacity 0.45s ease;
  }

  ${({ isTypingDisplay, isFullscreen }) =>
    (isTypingDisplay || isFullscreen) &&
    `
      display: none;
    `}

  @media (min-width: 1600px) {
    padding: 0.42rem 1.75rem;
    left: 1.5rem;
    right: 1.5rem;
  }

  /* “Awkward middle” widths: lots of nav links + Follow bar + Report — avoid clipping past the bar edge */
  @media (max-width: 1520px) and (min-width: 1281px) {
    left: 0.55rem;
    right: 0.55rem;
    padding: 0.34rem 0.58rem;
    gap: 0.32rem;
  }

  @media (max-width: 1280px) {
    padding: 0.4rem 1.1rem;
    left: 1rem;
    right: 1rem;
    top: 6px;
    border-radius: 13px;

    &::before { border-radius: 13px 13px 0 0; }
  }

  @media (max-width: 768px) {
    padding: 0.4rem 0.9rem;
    left: 0.5rem;
    right: 0.5rem;
    top: 5px;
    border-radius: 12px;

    &::before { border-radius: 12px 12px 0 0; }
    &::after { left: 15%; right: 15%; }
  }

  @media (max-width: 480px) {
    padding: 0.4rem 0.65rem;
    left: 0.4rem;
    right: 0.4rem;
    border-radius: 11px;

    &::before { border-radius: 11px 11px 0 0; }
    &::after { left: 10%; right: 10%; }
  }
`;

const Brand = styled(Link)`
  font-size: 1.08rem;
  font-weight: 700;
  background: linear-gradient(135deg, #fff 0%, #e0d2ff 40%, #c084fc 70%, #a855f7 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-transform: uppercase;
  letter-spacing: 2px;
  transition: all 0.35s cubic-bezier(0.23, 1, 0.32, 1);
  text-decoration: none;
  z-index: 1001;
  position: relative;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    filter: brightness(1.15);
    transform: scale(1.02);
    text-decoration: none;
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
    letter-spacing: 1.5px;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    letter-spacing: 1px;
  }
`;

const LivePill = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.28rem 0.6rem;
  border-radius: 999px;
  font-weight: 800;
  font-size: 0.75rem;
  color: #fff;
  text-decoration: none;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.95), rgba(220, 38, 38, 0.95));
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.4);
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  animation: pulse 2s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% { box-shadow: 0 4px 15px rgba(239, 68, 68, 0.4); }
    50% { box-shadow: 0 4px 20px rgba(239, 68, 68, 0.6); }
  }

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #fff;
    animation: blink 1s ease-in-out infinite;
    flex-shrink: 0;
  }

  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }

  &:hover {
    transform: scale(1.05);
    text-decoration: none;
  }

  @media (max-width: 768px) {
    padding: 0.3rem 0.6rem;
    font-size: 0.7rem;
    gap: 0.35rem;
    
    .dot {
      width: 5px;
      height: 5px;
    }
  }

  @media (max-width: 480px) {
    padding: 0.25rem 0.5rem;
    font-size: 0.65rem;
    gap: 0.3rem;
    
    .dot {
      width: 4px;
      height: 4px;
    }
  }
`;

const ReportIssueNavButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.26rem 0.6rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: rgba(255, 255, 255, 0.88);
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.14);
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  z-index: 1001;
  position: relative;
  min-height: 28px;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.22);
    color: #fff;
  }

  .label-long {
    display: inline;
  }
  .label-short {
    display: none;
  }

  /* Same breakpoint family as NavLink mid-range — full “Report issue” competes with Follow + auth cluster */
  @media (min-width: 1281px) and (max-width: 1520px) {
    .label-long {
      display: none;
    }
    .label-short {
      display: inline;
    }
    padding: 0.22rem 0.42rem;
    font-size: 0.66rem;
    gap: 0.22rem;
  }

  @media (min-width: 1521px) and (max-width: 1700px) {
    padding: 0.24rem 0.5rem;
    font-size: 0.69rem;
    gap: 0.24rem;
  }

  @media (max-width: 520px) {
    .label-long {
      display: none;
    }
    .label-short {
      display: inline;
    }
    padding: 0.28rem 0.5rem;
    font-size: 0.68rem;
  }

  @media (max-width: 380px) {
    .label-long,
    .label-short {
      display: none;
    }
    padding: 0.3rem 0.42rem;
    min-width: 32px;
    justify-content: center;
  }
`;

const ReportIssueNavButtonMobile = styled(ReportIssueNavButton)`
  @media (min-width: 1281px) {
    display: none;
  }
`;

const FollowReportCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-shrink: 0;
  margin-left: 4px;
  padding-left: 10px;
  border-left: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 1520px) and (min-width: 1281px) {
    gap: 0.28rem;
    margin-left: 0;
    padding-left: 6px;
  }
`;

const NavMenu = styled.ul`
  display: flex;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1;
  /* flex-start: overflow grows to the right; flex-end caused overlap on the logo at ~1300–1500px */
  justify-content: flex-start;
  min-width: 0;

  @media (min-width: 1281px) {
    flex-wrap: nowrap;
    overflow: visible;
    gap: 0.15rem;
  }

  @media (min-width: 1481px) {
    gap: 0;
  }

  @media (max-width: 1280px) {
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    height: 100vh;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(5, 5, 12, 0.88);
    backdrop-filter: blur(40px) saturate(200%);
    -webkit-backdrop-filter: blur(40px) saturate(200%);
    display: ${({ $isOpen }) => ($isOpen ? "flex" : "none")};
    padding: 80px 1rem 2rem;
    animation: ${slideDown} 0.35s cubic-bezier(0.23, 1, 0.32, 1);
    overflow-y: auto;
    overflow-x: hidden;
    z-index: 999;
    gap: 0.5rem;
  }
`;

const NavItem = styled.li`
  margin: 0 0.4rem;
  display: flex;
  align-items: center;

  @media (max-width: 1600px) {
    margin: 0 0.25rem;
  }

  @media (min-width: 1281px) and (max-width: 1520px) {
    margin: 0 0.12rem;
  }

  @media (max-width: 1280px) {
    margin: 0;
    width: 100%;
    max-width: 300px;
    text-align: center;
    box-sizing: border-box;
  }
`;

/* Standalone li (do not extend NavItem): avoids TDZ if const order changes in the bundle */
const NavItemFollowCluster = styled.li`
  margin: 0 0.4rem;

  @media (max-width: 1600px) {
    margin: 0 0.25rem;
  }

  @media (min-width: 1281px) and (max-width: 1520px) {
    margin: 0 0.12rem;
  }

  @media (max-width: 1280px) {
    display: none !important;
  }

  @media (min-width: 1281px) and (max-width: 1700px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.6) !important;
  font-weight: 400;
  font-size: 0.8rem;
  letter-spacing: 0.3px;
  text-transform: capitalize;
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
  padding: 0.28rem 0.5rem;
  min-height: 30px;
  line-height: 1.15;
  border-radius: 8px;
  position: relative;
  text-decoration: none;
  white-space: nowrap;

  @media (min-width: 1281px) and (max-width: 1520px) {
    font-size: 0.76rem;
    padding: 0.26rem 0.38rem;
    letter-spacing: 0.15px;
  }

  @media (min-width: 1281px) and (max-width: 1360px) {
    font-size: 0.72rem;
    padding: 0.24rem 0.3rem;
    letter-spacing: 0.08px;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 1px;
    left: 50%;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, #8b5cf6, #a78bfa);
    border-radius: 1px;
    transform: translateX(-50%);
    transition: width 0.3s cubic-bezier(0.23, 1, 0.32, 1);
    box-shadow: 0 0 6px rgba(139, 92, 246, 0.4);
    opacity: 0;
  }

  &:hover {
    color: #fff !important;
    text-decoration: none;
    background: rgba(255, 255, 255, 0.04);

    &::after {
      width: 55%;
      opacity: 1;
    }
  }

  &:active {
    transform: scale(0.96);
  }

  @media (max-width: 1280px) {
    padding: 1rem 1.5rem;
    font-size: 1.1rem;
    font-weight: 500;
    letter-spacing: 0;
    display: block;
    width: 100%;
    color: rgba(255, 255, 255, 0.9) !important;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px;

    &::after {
      display: none;
    }
    
    &:hover, &:active {
      background: rgba(139, 92, 246, 0.15);
      border-color: rgba(139, 92, 246, 0.3);
      transform: none;
    }
  }
`;

const SignUpLink = styled(Link)`
  display: inline-block;
  color: #fff !important;
  font-weight: 500;
  font-size: 0.78rem;
  letter-spacing: 0.3px;
  text-decoration: none;
  padding: 0.32rem 0.95rem;
  border-radius: 20px;
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 50%, #6366f1 100%);
  background-size: 200% 100%;
  white-space: nowrap;
  transition: all 0.35s cubic-bezier(0.23, 1, 0.32, 1);
  box-shadow:
    0 2px 12px rgba(139, 92, 246, 0.3),
    0 1px 0 0 rgba(255, 255, 255, 0.15) inset;
  position: relative;

  &:hover {
    transform: translateY(-1px) scale(1.03);
    box-shadow:
      0 6px 20px rgba(139, 92, 246, 0.45),
      0 1px 0 0 rgba(255, 255, 255, 0.2) inset;
    background-position: 100% 0;
    color: #fff !important;
    text-decoration: none;
  }

  &:active {
    transform: translateY(0) scale(0.97);
    box-shadow: 0 2px 8px rgba(139, 92, 246, 0.3);
  }

  @media (max-width: 1280px) {
    padding: 1rem 1.5rem;
    font-size: 1.1rem;
    display: block;
    width: 100%;
    text-align: center;
    border-radius: 12px;
    box-sizing: border-box;
  }
`;

// const LogoutLink = styled.a` // Unused
//   color: #ff6f61 !important;
//   font-weight: 500;
//   cursor: pointer;
//   transition: all 0.3s ease;
//   text-decoration: none;
//   padding: 0.5rem 0.75rem;
//   border-radius: 8px;

//   &:hover {
//     color: #e65b50 !important;
//     text-decoration: none;
//     background: rgba(255, 111, 97, 0.1);
//   }

//   @media (max-width: 1280px) {
//     display: block;
//     width: 100%;
//     padding: 1rem 1.5rem;
//     background: rgba(255, 111, 97, 0.1);
//     border: 1px solid rgba(255, 111, 97, 0.3);
//     border-radius: 12px;
//     text-align: center;
//     font-size: 1.1rem;
//   }
// `;

// Dropdown Components
const DropdownContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-shrink: 0;

  @media (min-width: 1281px) {
    z-index: 2;
    /* Keeps :hover active while moving through the gap above the menu */
    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 0;
      width: 100%;
      height: 10px;
      z-index: 1002;
    }
  }

  @media (max-width: 1280px) {
    width: 100%;
    max-width: 300px;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 0;
  }
`;

const DropdownTrigger = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-weight: 400;
  font-size: 0.8rem;
  letter-spacing: 0.3px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0.28rem 0.5rem;
  min-height: 30px;
  line-height: 1.15;
  white-space: nowrap;
  border-radius: 8px;
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);

  @media (min-width: 1281px) and (max-width: 1520px) {
    font-size: 0.76rem;
    padding: 0.26rem 0.38rem;
    letter-spacing: 0.15px;
    gap: 3px;
  }

  @media (min-width: 1281px) and (max-width: 1360px) {
    font-size: 0.72rem;
    padding: 0.24rem 0.3rem;
    letter-spacing: 0.08px;
    gap: 2px;
  }

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.04);
  }

  &:active {
    transform: scale(0.96);
  }

  svg {
    transition: transform 0.35s cubic-bezier(0.23, 1, 0.32, 1);
    opacity: 0.4;
  }

  &:hover svg {
    transform: rotate(180deg);
    opacity: 0.7;
  }

  @media (max-width: 1280px) {
    justify-content: center;
    padding: 1rem 1.5rem;
    border-radius: 12px;
    font-weight: 500;
    letter-spacing: 0;
    background: ${({ $isOpen }) => $isOpen ? 'rgba(139, 92, 246, 0.15)' : 'rgba(255, 255, 255, 0.04)'};
    border: 1px solid ${({ $isOpen }) => $isOpen ? 'rgba(139, 92, 246, 0.3)' : 'rgba(255, 255, 255, 0.08)'};
    font-size: 1.1rem;
    width: 100%;
    
    &:active { transform: none; }
    
    svg {
      transform: ${({ $isOpen }) => $isOpen ? 'rotate(180deg)' : 'rotate(0)'};
      opacity: 0.6;
    }
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 8px;
  transform: translateY(6px) scale(0.98);
  transform-origin: top left;
  background: rgba(14, 14, 30, 0.76);
  backdrop-filter: blur(40px) saturate(200%);
  -webkit-backdrop-filter: blur(40px) saturate(200%);
  border-radius: 18px;
  box-shadow:
    0 20px 56px rgba(0, 0, 0, 0.44),
    0 1.5px 0 0 rgba(255, 255, 255, 0.04) inset,
    0 0 0 0.5px rgba(255, 255, 255, 0.08),
    0 0 34px rgba(139, 92, 246, 0.16);
  min-width: 220px;
  padding: 8px;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.3s cubic-bezier(0.23, 1, 0.32, 1), visibility 0.3s cubic-bezier(0.23, 1, 0.32, 1),
    transform 0.3s cubic-bezier(0.23, 1, 0.32, 1);
  z-index: 1003;
  border: 1px solid rgba(139, 92, 246, 0.24);
  max-height: 80vh;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;

  ${DropdownContainer}:hover & {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transform: translateY(0) scale(1);
  }

  @media (min-width: 1281px) {
    ${({ $isOpen }) =>
    $isOpen
      ? `
      opacity: 1;
      visibility: visible;
      pointer-events: auto;
      transform: translateY(0) scale(1);
    `
      : ''}
  }

  @media (max-width: 1280px) {
    position: relative;
    left: 0;
    right: 0;
    top: auto;
    margin-top: 8px;
    transform: none;
    box-shadow: none;
    border: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(15, 15, 25, 0.9);
    border-radius: 10px;
    padding: 6px;
    min-width: unset;
    width: 100%;
    box-sizing: border-box;
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    max-height: ${({ $isOpen }) => ($isOpen ? '80vh' : '0')};
    overflow-y: ${({ $isOpen }) => ($isOpen ? 'auto' : 'hidden')};
    overflow-x: hidden;
    transition: max-height 0.3s ease;
    display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
    -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */

    ${DropdownContainer}:hover & {
      transform: none;
    }
  }
`;

const DropdownItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  color: rgba(255, 255, 255, 0.65) !important;
  text-decoration: none;
  font-size: 0.84rem;
  font-weight: 400;
  transition: all 0.25s cubic-bezier(0.23, 1, 0.32, 1);
  border-radius: 10px;

  &:hover {
    background: linear-gradient(135deg, rgba(168, 85, 247, 0.16) 0%, rgba(99, 102, 241, 0.09) 100%);
    color: #fff !important;
    text-decoration: none;
    transform: translateX(1px);
  }

  &:active {
    background: rgba(255, 255, 255, 0.1);
    transform: scale(0.98);
  }

  .icon {
    font-size: 1rem;
    width: 24px;
    height: 24px;
    border-radius: 8px;
    background: rgba(139, 92, 246, 0.18);
    text-align: center;
    flex-shrink: 0;
    color: #d8b4fe;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .label {
    flex: 1;
    white-space: nowrap;
  }

  .badge {
    background: linear-gradient(135deg, #ff6f61 0%, #ff9500 100%);
    color: white;
    font-size: 0.65rem;
    padding: 2px 6px;
    border-radius: 10px;
    font-weight: 600;
    flex-shrink: 0;
  }

  @media (max-width: 1280px) {
    padding: 12px 16px;
    margin: 2px 0;
    justify-content: flex-start;
    width: 100%;
    box-sizing: border-box;
    
    .icon {
      display: inline-block;
      min-width: 28px;
    }
    
    .label {
      flex: 1;
    }
    
    &:active {
      background: linear-gradient(135deg, #fff5f5 0%, #fff0ed 100%);
    }
  }
`;

const DropdownActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  color: rgba(255, 255, 255, 0.65);
  background: transparent;
  border: none;
  text-decoration: none;
  font-size: 0.84rem;
  font-weight: 400;
  transition: all 0.25s cubic-bezier(0.23, 1, 0.32, 1);
  border-radius: 10px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: linear-gradient(135deg, rgba(168, 85, 247, 0.16) 0%, rgba(99, 102, 241, 0.09) 100%);
    color: #fff;
    transform: translateX(1px);
  }

  &:active {
    background: rgba(255, 255, 255, 0.1);
    transform: scale(0.98);
  }

  .icon {
    font-size: 1rem;
    width: 24px;
    height: 24px;
    border-radius: 8px;
    background: rgba(139, 92, 246, 0.18);
    text-align: center;
    flex-shrink: 0;
    color: #d8b4fe;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .label {
    flex: 1;
    white-space: nowrap;
  }

  @media (max-width: 1280px) {
    padding: 12px 16px;
    margin: 2px 0;
    justify-content: flex-start;
    width: 100%;
    box-sizing: border-box;

    .icon {
      display: inline-block;
      min-width: 28px;
    }

    .label {
      flex: 1;
    }
  }
`;

const DropdownDivider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  margin: 6px 0;
`;

const Hamburger = styled.button`
  display: none;
  flex-direction: column;
  cursor: pointer;
  z-index: 1010;
  gap: 5px;
  padding: 8px;
  border-radius: 8px;
  border: none;
  background: transparent;
  transition: all 0.3s ease;
  flex-shrink: 0;
  font: inherit;
  color: inherit;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  span {
    width: 20px;
    height: 2px;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 2px;
    transition: all 0.3s ease;
    display: block;
  }

  ${({ $isOpen }) => $isOpen && `
    position: fixed;
    top: 1.25rem;
    right: 1.5rem;
    background: rgba(139, 92, 246, 0.3);
    border-radius: 10px;
    padding: 10px;
    
    span {
      width: 22px;
      background: #fff;
    }
    
    span:nth-child(1) {
      transform: rotate(45deg) translate(5px, 5px);
    }
    span:nth-child(2) {
      opacity: 0;
      transform: scaleX(0);
    }
    span:nth-child(3) {
      transform: rotate(-45deg) translate(5px, -5px);
    }
  `}

  @media (max-width: 1280px) {
    display: flex;
  }
`;

const UserMenu = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
  margin-left: 4px;
  padding-left: 8px;
  border-left: 1px solid rgba(255, 255, 255, 0.08);

  @media (max-width: 1520px) and (min-width: 1281px) {
    gap: 0.12rem;
    padding-left: 6px;
    margin-left: 2px;
  }

  @media (min-width: 1281px) {
    margin-left: auto;
    flex-shrink: 0;
  }

  @media (max-width: 1280px) {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
    max-width: 300px;
    padding: 1rem 0;
    margin-left: 0;
    border-left: none;
    text-align: center;
    margin-top: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
  }
`;

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: ${({ isTypingDisplay }) => isTypingDisplay ? 'transparent' : '#050507'};
  color: #fff;
  font-family: "Inter", sans-serif;
  overflow-x: hidden;
`;

const Content = styled.div`
  flex: 1;
  padding: 0;
  margin: 0;
  width: 100%;
  background: ${({ isTypingDisplay }) => isTypingDisplay ? 'transparent' : 'transparent'};
`;


function JobResultsLegacyRedirect() {
  const { id } = useParams();
  return <Navigate to={`/job-updates/${id}`} replace />;
}

function App() {
  const navigate = useNavigate();
  const { isFeatureEnabled } = useFeatureFlags();
  const {
    isTenantHost,
    features: tenantFeatures,
    loading: tenantEntitlementsLoading,
    subscriptionLapsed: tenantSubscriptionLapsed,
    branding: tenantBranding,
  } = useTenantEntitlements();
  const brandName = useBrandName();
  const [showModeratorBoard, setShowModeratorBoard] = useState(false);
  const [showAdminBoard, setShowAdminBoard] = useState(false);
  const [currentUser, setCurrentUser] = useState(undefined);
  const [liveExam, setLiveExam] = useState(null);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [showReportIssueModal, setShowReportIssueModal] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [loadCookieConsent, setLoadCookieConsent] = useState(false);
  const location = useLocation();
  const isTypingDisplay = location.pathname === "/typing-display";

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    // Defer Bootstrap CSS so home LCP path avoids 250KB+ unused stylesheet.
    if (location.pathname !== "/" && location.pathname !== "/home") {
      import("bootstrap/dist/css/bootstrap.min.css");
    }
  }, [location.pathname]);

  useEffect(() => {
    const timer = setTimeout(() => setLoadCookieConsent(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  const isTypingDisplayTest = location.pathname === "/typing-display-test";
  const isLessonPlayer = location.pathname.startsWith("/learn/lesson/");
  const isHomePage = location.pathname === "/" || location.pathname === "/home";
  const isJuniorPage = location.pathname.startsWith("/junior");
  const isGamePage = location.pathname.startsWith("/games/");
  const isInstitutionalPath = location.pathname.startsWith("/institutional");
  const isSuperAdminPath = location.pathname.startsWith("/super-admin");
  const isPlannerPath = location.pathname.startsWith("/planner");
  const isJobUpdatesPath = location.pathname.startsWith("/job-updates");
  const institutionOnlyUser =
    currentUser && isInstitutionOnlyAdmin(currentUser.roles);
  const institutionHomeLearner =
    currentUser && isInstitutionHomeLearner(currentUser);
  // const { bannerBackground } = useContext(BannerContext); // Unused

  useEffect(() => {
    // Capture referral code from URL if present
    ReferralService.storeReferralFromUrl();

    // Validate session on mount
    const user = AuthService.getCurrentUser();
    if (user) {
      // Check if session is still valid
      if (!validateSession()) {
        // Token expired or app version changed - clear session
        clearSessionAndRedirect();
        return;
      }

      setCurrentUser(user);
      setShowModeratorBoard(user.roles.includes("ROLE_MODERATOR"));
      setShowAdminBoard(user.roles.includes("ROLE_ADMIN") || user.roles.includes("ROLE_SUPER_ADMIN"));

      if (!localStorage.getItem('telegram_prompted')) {
        const skipTelegramForInstitutionOnly =
          user &&
          (isInstitutionOnlyAdmin(user.roles) || isInstitutionHomeLearner(user));
        if (!skipTelegramForInstitutionOnly) {
          setTimeout(() => setShowTelegramModal(true), 1500);
        }
      }
    }

    EventBus.on("logout", logOut);

    // Check token validity on user activity (no periodic calls)
    // HTTP interceptors will catch 401 on actual API calls
    const checkToken = async () => {
      // Skip check if tab is hidden
      if (document.hidden) {
        return;
      }

      const currentUser = AuthService.getCurrentUser();
      if (currentUser) {
        // First check local validation (expiration, version) - no API call
        if (!validateSession()) {
          clearSessionAndRedirect();
          return;
        }

        // Slide JWT expiry before hard timeout (multi-day tabs / long tests)
        if (shouldRefreshAccessToken(currentUser.accessToken)) {
          try {
            await AuthService.refreshToken();
          } catch {
            /* fall through to validateToken */
          }
        }

        // Then validate with server (checks token version) - only when user is active
        try {
          const isValid = await AuthService.validateToken();
          if (!isValid) {
            // Token invalid on server (e.g., logged in from another device)
            clearSessionAndRedirect();
          }
        } catch (error) {
          // If validation fails (401), the interceptor will handle logout
          if (error.response && error.response.status === 401) {
            clearSessionAndRedirect();
          }
        }
      }
    };

    // Check immediately on mount
    checkToken();

    // Check when user becomes active (tab becomes visible)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Tab became visible - check token immediately
        checkToken();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Check on user activity (mouse click, keypress) - but throttle it
    let lastActivityCheck = 0;
    const ACTIVITY_CHECK_THROTTLE = 60 * 1000; // Only check once per minute on activity
    const handleUserActivity = () => {
      const now = Date.now();
      if (now - lastActivityCheck > ACTIVITY_CHECK_THROTTLE) {
        lastActivityCheck = now;
        checkToken();
      }
    };
    window.addEventListener('mousedown', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);

    return () => {
      EventBus.remove("logout");
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('mousedown', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
    };
  }, []);

  // Institution-only admins: keep them on the institutional portal (not the public home/courses shell).
  useEffect(() => {
    const user = AuthService.getCurrentUser();
    if (!user || !isInstitutionOnlyAdmin(user.roles)) return;
    if (!isPathAllowedForInstitutionOnlyUser(location.pathname)) {
      navigate("/institutional/dashboard", { replace: true });
    }
  }, [location.pathname, navigate]);

  // School-branded learners: restrict to allowed paths based on their plan's features.
  useEffect(() => {
    const user = AuthService.getCurrentUser();
    if (!user || !isInstitutionHomeLearner(user)) return;
    if (!isPathAllowedForInstitutionHomeLearner(location.pathname, tenantFeatures)) {
      navigate("/institutional/student", { replace: true });
    }
  }, [location.pathname, navigate, tenantFeatures]);

  // Tenant host (school subdomain): hide routes not included in institution subscription plan (CORE / PLUS / FULL).
  useEffect(() => {
    if (!isTenantHost || tenantEntitlementsLoading) return;
    const path = location.pathname;
    if (path.startsWith("/institutional")) return;
    if (
      path.startsWith("/admin") ||
      path.startsWith("/mod") ||
      path.startsWith("/super-admin")
    ) {
      return;
    }

    const goHome = () => navigate("/home", { replace: true });

    if (
      !tenantFeatures.practice &&
      (path === "/typing-display" ||
        path === "/typing-display-test" ||
        path.startsWith("/typing-display/"))
    ) {
      goHome();
      return;
    }
    if (!tenantFeatures.tests && (path.startsWith("/start-test") || path.startsWith("/typing-test"))) {
      goHome();
      return;
    }
    if (!tenantFeatures.typingLessons && path.startsWith("/learn")) {
      goHome();
      return;
    }
    if (!tenantFeatures.steno && path.startsWith("/steno")) {
      goHome();
      return;
    }
    if (!tenantFeatures.stenoLessons && path.startsWith("/steno/learn")) {
      goHome();
      return;
    }
    if (!tenantFeatures.playHub) {
      if (
        path.startsWith("/games") ||
        path === "/daily-challenge" ||
        path === "/race" ||
        path.startsWith("/tournaments") ||
        path.startsWith("/live-exams") ||
        path.startsWith("/achievements") ||
        path.startsWith("/leaderboard") ||
        path.startsWith("/social-tasks")
      ) {
        goHome();
        return;
      }
    }
    if (!tenantFeatures.blog && path.startsWith("/blog")) {
      goHome();
      return;
    }
    if (!tenantFeatures.community && path.startsWith("/community")) {
      goHome();
      return;
    }
    if (!tenantFeatures.referral && path.startsWith("/referral")) {
      goHome();
      return;
    }
    if (!tenantFeatures.socialFriends && path.startsWith("/friends")) {
      goHome();
      return;
    }
  }, [
    isTenantHost,
    tenantEntitlementsLoading,
    tenantFeatures.practice,
    tenantFeatures.tests,
    tenantFeatures.typingLessons,
    tenantFeatures.steno,
    tenantFeatures.stenoLessons,
    tenantFeatures.playHub,
    tenantFeatures.blog,
    tenantFeatures.community,
    tenantFeatures.referral,
    tenantFeatures.socialFriends,
    location.pathname,
    navigate,
  ]);

  // Live exam indicator (navbar) using smart polling
  // Optimized for 30K+ users - no SSE connections held open
  // - Polls every 60 seconds when tab is visible
  // - Pauses polling when tab is hidden (saves resources)
  // - Uses cache to reduce API calls
  useEffect(() => {
    if (!isFeatureEnabled("live_exams")) {
      setLiveExam(null);
      return;
    }

    let mounted = true;
    let pollTimer = null;
    const POLL_INTERVAL = 60000; // 60 seconds
    const CACHE_KEY = 'liveExamCache';
    const CACHE_DURATION = 30000; // 30 second cache

    const fetchLiveExam = async () => {
      if (!mounted) return;

      // Check cache first
      try {
        const cached = sessionStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_DURATION) {
            setLiveExam(data);
            return;
          }
        }
      } catch (e) { /* ignore cache errors */ }

      try {
        const res = await LiveExamService.getActiveLiveExams();
        if (!mounted) return;
        const items = res.data || [];
        const inProgress = items.find((e) => e.status === "IN_PROGRESS") || null;
        setLiveExam(inProgress);

        // Update cache
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({
          data: inProgress,
          timestamp: Date.now()
        }));
      } catch (err) {
        if (mounted) setLiveExam(null);
      }
    };

    const startPolling = () => {
      fetchLiveExam();
      pollTimer = setInterval(fetchLiveExam, POLL_INTERVAL);
    };

    const stopPolling = () => {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    // Visibility API - pause polling when tab is hidden
    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        startPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    // Start polling if tab is visible
    if (!document.hidden) {
      startPolling();
    }

    return () => {
      mounted = false;
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isFeatureEnabled]);

  useEffect(() => {
    const handleFullscreenChange = () =>
      setIsFullscreen(document.fullscreenElement !== null);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const logOut = () => {
    AuthService.logout();
    setIsNavOpen(false);
    setShowModeratorBoard(false);
    setShowAdminBoard(false);
    setCurrentUser(undefined);
  };

  const toggleNav = () => {
    setIsNavOpen(!isNavOpen);
    setOpenDropdown(null); // Close dropdowns when closing menu
  };

  const toggleDropdown = (name) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  /** Close mobile overlay + dropdowns. Must match NavMenu/Hamburger breakpoint (max-width: 1280px). Always clearing state is harmless on desktop. */
  const closeMenuOnMobile = () => {
    setIsNavOpen(false);
    setOpenDropdown(null);
  };

  useEffect(() => {
    setIsNavOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  // Practice and exam pages should be distraction-free: always hide navbar
  // Also hide for junior pages (they have their own navigation)
  // Institution-only admins: hide the main Typogram navbar on /institutional/* (they use the dashboard chrome only)
  const shouldHideNavbar =
    location.pathname === '/video-guides' ||
    location.pathname === '/get-started' ||
    isTypingDisplay ||
    isTypingDisplayTest ||
    isLessonPlayer ||
    isJuniorPage ||
    (institutionOnlyUser && isInstitutionalPath) ||
    isSuperAdminPath ||
    isPlannerPath ||
    isJobUpdatesPath ||
    isFullscreen;

  return (
    <AppContainer
      isTypingDisplay={isTypingDisplay}
      isFullscreen={shouldHideNavbar}
      isHomePage={isHomePage}
    >
      {isTenantHost &&
        tenantSubscriptionLapsed &&
        !tenantEntitlementsLoading &&
        !shouldHideNavbar && (
          <div
            role="status"
            style={{
              background: "linear-gradient(90deg, #7c3aed 0%, #5b21b6 100%)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "40px",
              padding: "0 1rem",
              fontSize: "0.85rem",
              fontWeight: 500,
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              zIndex: 2000,
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}
          >
            Your school's access is paused (subscription inactive or expired). Contact your institution administrator.
          </div>
        )}
      
      {!isGamePage && !isTenantHost && !isInstitutionalPath && !isSuperAdminPath && !shouldHideNavbar && (
        <Suspense fallback={null}>
          <GlobalExamBanner />
          {!isHomePage && <SocialProofToast />}
        </Suspense>
      )}

      {!shouldHideNavbar && !isGamePage && (
        <Navbar 
          isHomePage={isHomePage} 
          $scrolled={isScrolled} 
          $hasBanner={isTenantHost && tenantSubscriptionLapsed && !tenantEntitlementsLoading}
        >
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            flexShrink: 0
          }}>
            <Brand to={institutionHomeLearner ? "/institutional/student" : "/"}>
              {isTenantHost && tenantBranding?.logoUrl ? (
                <img src={tenantBranding.logoUrl} alt={brandName} style={{ height: 28, objectFit: 'contain', verticalAlign: 'middle' }} />
              ) : (
                brandName.toUpperCase()
              )}
            </Brand>
            {liveExam &&
              isFeatureEnabled("live_exams") &&
              (!institutionHomeLearner || (isTenantHost && tenantFeatures.playHub)) &&
              (!isTenantHost || tenantFeatures.playHub) && (
                <LivePill
                  to={currentUser ? "/live-exams" : "/login"}
                  title={currentUser ? "Live exams are running — click to view" : "Login to join live exams"}
                >
                  <span className="dot" />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    LIVE
                  </span>
                </LivePill>
              )}
            {isTenantHost ? (
              <ReportIssueNavButton
                type="button"
                onClick={() => setShowReportIssueModal(true)}
                title={(institutionHomeLearner || isTenantHost || institutionOnlyUser) ? "Thoughts for Typogram" : "Report an issue or send feedback"}
              >
                <span aria-hidden>💬</span>
                <span className="label-long">{(institutionHomeLearner || isTenantHost || institutionOnlyUser) ? "Thoughts for Typogram" : "Report issue"}</span>
                <span className="label-short">{(institutionHomeLearner || isTenantHost || institutionOnlyUser) ? "Thoughts" : "Issue"}</span>
              </ReportIssueNavButton>
            ) : (
              <ReportIssueNavButtonMobile
                type="button"
                onClick={() => setShowReportIssueModal(true)}
                title={(institutionHomeLearner || isTenantHost || institutionOnlyUser) ? "Thoughts for Typogram" : "Report an issue or send feedback"}
              >
                <span aria-hidden>💬</span>
                <span className="label-long">{(institutionHomeLearner || isTenantHost || institutionOnlyUser) ? "Thoughts for Typogram" : "Report issue"}</span>
                <span className="label-short">{(institutionHomeLearner || isTenantHost || institutionOnlyUser) ? "Thoughts" : "Issue"}</span>
              </ReportIssueNavButtonMobile>
            )}
          </div>
          <Hamburger
            type="button"
            $isOpen={isNavOpen}
            onClick={(e) => {
              e.stopPropagation();
              toggleNav();
            }}
            aria-expanded={isNavOpen}
            aria-controls="typogram-main-nav"
            aria-label={isNavOpen ? "Close menu" : "Open menu"}
          >
            <span></span>
            <span></span>
            <span></span>
          </Hamburger>
          <NavMenu id="typogram-main-nav" $isOpen={isNavOpen}>
            <NavItem>
              <NavLink
                to={institutionHomeLearner ? "/institutional/student" : (isTenantHost ? "/login" : "/home")}
                onClick={closeMenuOnMobile}
              >
                {institutionHomeLearner ? "School home" : "Home"}
              </NavLink>
            </NavItem>
            {(!institutionHomeLearner || (isTenantHost && tenantFeatures.playHub)) && (
              <NavItem>
                <DropdownContainer>
                  <DropdownTrigger
                    $isOpen={openDropdown === 'play'}
                    onClick={() => toggleDropdown('play')}
                  >
                    <Gamepad2 size={18} style={{ marginRight: "6px" }} /> Play <span aria-hidden>▾</span>
                  </DropdownTrigger>
                  <DropdownMenu $isOpen={openDropdown === 'play'}>
                    {isFeatureEnabled("daily_challenges") && (
                      <DropdownItem to="/daily-challenge" onClick={closeMenuOnMobile}>
                        <span className="icon"><Flame size={16} /></span>
                        <span className="label">Daily Challenge</span>
                        <span className="badge">NEW</span>
                      </DropdownItem>
                    )}
                    {isFeatureEnabled("typing_races") && (
                      <DropdownItem to="/race" onClick={closeMenuOnMobile}>
                        <span className="icon"><Flag size={16} /></span>
                        <span className="label">Race Mode</span>
                      </DropdownItem>
                    )}
                    <DropdownItem to="/games" onClick={closeMenuOnMobile}>
                      <span className="icon"><Gamepad2 size={16} /></span>
                      <span className="label">Typing Games</span>
                    </DropdownItem>
                    {isFeatureEnabled("tournaments") && (
                      <DropdownItem to="/tournaments" onClick={closeMenuOnMobile}>
                        <span className="icon"><Trophy size={16} /></span>
                        <span className="label">Tournaments</span>
                      </DropdownItem>
                    )}
                    {isFeatureEnabled("live_exams") && (
                      <DropdownItem to="/live-exams" onClick={closeMenuOnMobile}>
                        <span className="icon"><Activity size={16} /></span>
                        <span className="label">Live Exams</span>
                      </DropdownItem>
                    )}
                    {(isFeatureEnabled("achievements") || isFeatureEnabled("leaderboard") || isFeatureEnabled("social_tasks")) && <DropdownDivider />}
                    {isFeatureEnabled("social_tasks") && (
                      <DropdownItem to="/social-tasks" onClick={closeMenuOnMobile}>
                        <span className="icon">🎯</span>
                        <span className="label">Missions</span>
                        <span className="badge">XP</span>
                      </DropdownItem>
                    )}
                    {isFeatureEnabled("achievements") && (
                      <DropdownItem to="/achievements" onClick={closeMenuOnMobile}>
                        <span className="icon">🎖️</span>
                        <span className="label">Achievements</span>
                      </DropdownItem>
                    )}
                    {isFeatureEnabled("leaderboard") && (
                      <DropdownItem to="/leaderboard" onClick={closeMenuOnMobile}>
                        <span className="icon"><BarChart2 size={16} /></span>
                        <span className="label">Leaderboards</span>
                      </DropdownItem>
                    )}
                  </DropdownMenu>
                </DropdownContainer>
              </NavItem>
            )}
            {isFeatureEnabled("typing_lessons") && (!isTenantHost || tenantFeatures.typingLessons) && (
              <NavItem>
                <NavLink to="/learn" onClick={closeMenuOnMobile}>Lessons</NavLink>
              </NavItem>
            )}
            {(!isTenantHost || tenantFeatures.practice) && (
              <NavItem>
                <NavLink to="/typing-display" onClick={closeMenuOnMobile} data-tour="nav-practice">Practice</NavLink>
              </NavItem>
            )}
            {(!isTenantHost || tenantFeatures.courses) && (
              <NavItem>
                <NavLink to="/courses" onClick={closeMenuOnMobile} data-tour="nav-courses">Courses</NavLink>
              </NavItem>
            )}
            {(!isTenantHost || tenantFeatures.tests || tenantFeatures.courses || tenantFeatures.testPasses) && (
              <NavItem>
                <DropdownContainer>
                  <DropdownTrigger
                    $isOpen={openDropdown === "typingTest"}
                    onClick={() => toggleDropdown("typingTest")}
                    data-tour="nav-typing-test"
                  >
                    <Keyboard size={18} style={{ marginRight: '6px' }} /> Typing test <span aria-hidden>▾</span>
                  </DropdownTrigger>
                  <DropdownMenu $isOpen={openDropdown === "typingTest"}>
                    {(!isTenantHost || tenantFeatures.courses) && (
                      <DropdownItem to="/courses" onClick={closeMenuOnMobile}>
                        <span className="icon"><BookOpen size={16} /></span>
                        <span className="label">Courses</span>
                      </DropdownItem>
                    )}
                    {(!isTenantHost || tenantFeatures.tests) && (
                      currentUser ? (
                        <DropdownItem to="/start-test" onClick={closeMenuOnMobile}>
                          <span className="icon"><FileText size={16} /></span>
                          <span className="label">Test</span>
                        </DropdownItem>
                      ) : (
                        <DropdownItem to="/start-test" state={{ isDemo: true }} onClick={closeMenuOnMobile}>
                          <span className="icon"><Target size={16} /></span>
                          <span className="label">Demo</span>
                        </DropdownItem>
                      )
                    )}
                    {(!isTenantHost || tenantFeatures.testPasses) && (
                      <DropdownItem to="/typing-test/pricing" onClick={closeMenuOnMobile}>
                        <span className="icon"><CreditCard size={16} /></span>
                        <span className="label">Test passes</span>
                      </DropdownItem>
                    )}
                  </DropdownMenu>
                </DropdownContainer>
              </NavItem>
            )}
            {isFeatureEnabled("steno") && (!isTenantHost || tenantFeatures.steno) && (
              <NavItem>
                <DropdownContainer>
                  <DropdownTrigger
                    $isOpen={openDropdown === 'steno'}
                    onClick={() => toggleDropdown('steno')}
                  >
                    <PenTool size={18} style={{ marginRight: '6px' }} /> Steno <span aria-hidden>▾</span>
                  </DropdownTrigger>
                  <DropdownMenu $isOpen={openDropdown === 'steno'}>
                    <DropdownItem to="/steno" onClick={closeMenuOnMobile}>
                      <span className="icon"><Headphones size={16} /></span>
                      <span className="label">Practice Hub</span>
                    </DropdownItem>
                    <DropdownItem to="/courses?type=steno" onClick={closeMenuOnMobile}>
                      <span className="icon"><BookOpen size={16} /></span>
                      <span className="label">Steno Courses</span>
                    </DropdownItem>
                    <DropdownItem to="/steno/pricing" onClick={closeMenuOnMobile}>
                      <span className="icon"><CreditCard size={16} /></span>
                      <span className="label">Steno Pass</span>
                    </DropdownItem>
                    {(!isTenantHost || tenantFeatures.stenoLessons) && (
                      <DropdownItem to="/steno/learn" onClick={closeMenuOnMobile}>
                        <span className="icon"><BookOpen size={16} /></span>
                        <span className="label">Learn Shorthand</span>
                      </DropdownItem>
                    )}
                  </DropdownMenu>
                </DropdownContainer>
              </NavItem>
            )}
            {isFeatureEnabled("blog_system") &&
              (!institutionHomeLearner || (isTenantHost && tenantFeatures.blog)) &&
              (!isTenantHost || tenantFeatures.blog) && (
                <NavItem>
                  <NavLink to="/blog" onClick={closeMenuOnMobile}>Blog</NavLink>
                </NavItem>
              )}

            {isFeatureEnabled("study_schedule") &&
              (!institutionHomeLearner || (isTenantHost && tenantFeatures.studySchedule)) &&
              (!isTenantHost || tenantFeatures.studySchedule) && (
                <NavItem>
                  <NavLink to={EXAM_PREP_PATH} onClick={() => {trackMockFunnel("mock_discovery_click", {source:"navigation"}); closeMenuOnMobile();}}>{EXAM_PREP_LABEL}</NavLink>
                </NavItem>
              )}


            {isFeatureEnabled("system_design") && (
              <NavItem>
                <NavLink to="/system-design" onClick={closeMenuOnMobile}>System Design</NavLink>
              </NavItem>
            )}

            {!isTenantHost && (
              <NavItem>
                <DropdownContainer>
                  <DropdownTrigger
                    $isOpen={openDropdown === 'more'}
                    onClick={() => toggleDropdown('more')}
                  >
                    <MoreHorizontal size={18} style={{ marginRight: "6px" }} /> More <span aria-hidden>▾</span>
                  </DropdownTrigger>
                  <DropdownMenu $isOpen={openDropdown === 'more'}>
                    {isFeatureEnabled("sarkari_results") && (
                      <DropdownItem to="/job-updates" onClick={closeMenuOnMobile}>
                        <span className="icon"><Briefcase size={16} /></span>
                        <span className="label">Job Updates</span>
                      </DropdownItem>
                    )}
                    {isFeatureEnabled("pdf_tools") && (
                      <DropdownItem to="/pdf-tools" onClick={closeMenuOnMobile}>
                        <span className="icon"><FileText size={16} /></span>
                        <span className="label">PDF Tools</span>
                      </DropdownItem>
                    )}
                    <DropdownActionButton
                      type="button"
                      onClick={() => {
                        setShowReportIssueModal(true);
                        closeMenuOnMobile();
                      }}
                    >
                      <span className="icon"><MessageSquare size={16} /></span>
                      <span className="label">Report issue</span>
                    </DropdownActionButton>
                  </DropdownMenu>
                </DropdownContainer>
              </NavItem>
            )}
            {isFeatureEnabled("community_hub") &&
              (!institutionHomeLearner || (isTenantHost && tenantFeatures.community)) &&
              (!isTenantHost || tenantFeatures.community) && (
                <NavItem>
                  <NavLink to="/community" onClick={closeMenuOnMobile}>Community</NavLink>
                </NavItem>
              )}
            <UserMenu>
              {currentUser ? (
                <>
                  <NavItem>
                    <DropdownContainer>
                      <DropdownTrigger
                        $isOpen={openDropdown === 'user'}
                        onClick={() => toggleDropdown('user')}
                      >
                        <User size={18} style={{ marginTop: "3px" }} />
                        {currentUser.username}
                        <span aria-hidden>▾</span>
                      </DropdownTrigger>
                      <DropdownMenu $isOpen={openDropdown === 'user'}>
                        <DropdownItem to="/profile" onClick={closeMenuOnMobile}>
                          <span className="icon"><User size={16} /></span>
                          <span className="label">My Profile</span>
                        </DropdownItem>
                        <DropdownItem to="/user-result" onClick={closeMenuOnMobile}>
                          <span className="icon"><BarChart2 size={16} /></span>
                          <span className="label">My Results</span>
                        </DropdownItem>
                        {isFeatureEnabled("referral_system") &&
                          (!institutionHomeLearner || (isTenantHost && tenantFeatures.referral)) &&
                          (!isTenantHost || tenantFeatures.referral) && (
                            <DropdownItem to="/referral" onClick={closeMenuOnMobile}>
                              <span className="icon"><Gift size={16} /></span>
                              <span className="label">Refer & Earn</span>
                            </DropdownItem>
                          )}
                        {isFeatureEnabled("social_features") &&
                          (!institutionHomeLearner || (isTenantHost && tenantFeatures.socialFriends)) &&
                          (!isTenantHost || tenantFeatures.socialFriends) && (
                            <DropdownItem to="/friends" onClick={closeMenuOnMobile}>
                              <span className="icon"><Users size={16} /></span>
                              <span className="label">Friends & Chat</span>
                            </DropdownItem>
                          )}
                        {showModeratorBoard && (
                          <>
                            <DropdownDivider />
                            <DropdownItem to="/mod" onClick={closeMenuOnMobile}>
                              <span className="icon"><Shield size={16} /></span>
                              <span className="label">Moderator</span>
                            </DropdownItem>
                          </>
                        )}
                        {showAdminBoard && (
                          <>
                            <DropdownDivider />
                            <DropdownItem to="/institutional/dashboard" onClick={closeMenuOnMobile}>
                              <span className="icon"><Building size={16} /></span>
                              <span className="label">Institution Ops</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/study-subscription" onClick={closeMenuOnMobile}>
                              <span className="icon"><BookOpen size={16} /></span>
                              <span className="label">Study Ops</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/telegram-marketing" onClick={closeMenuOnMobile}>
                              <span className="icon"><Send size={16} /></span>
                              <span className="label">Distribution Ops</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/marketing-config" onClick={closeMenuOnMobile}>
                              <span className="icon"><Megaphone size={16} /></span>
                              <span className="label">Marketing & Promos</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/blog-seo" onClick={closeMenuOnMobile}>
                              <span className="icon"><TrendingUp size={16} /></span>
                              <span className="label">SEO Ops</span>
                            </DropdownItem>
                            <DropdownDivider />
                            <DropdownItem to="/blog-admin" onClick={closeMenuOnMobile}>
                              <span className="icon"><Settings size={16} /></span>
                              <span className="label">Blog Admin</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/tournaments" onClick={closeMenuOnMobile}>
                              <span className="icon"><Trophy size={16} /></span>
                              <span className="label">Tournament Admin</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/live-exams" onClick={closeMenuOnMobile}>
                              <span className="icon"><Activity size={16} /></span>
                              <span className="label">Live Exam Admin</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/steno" onClick={closeMenuOnMobile}>
                              <span className="icon"><PenTool size={16} /></span>
                              <span className="label">Steno Admin</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/feedback" onClick={closeMenuOnMobile}>
                              <span className="icon"><MessageSquare size={16} /></span>
                              <span className="label">Feedback Moderation</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/typing-test-plans" onClick={closeMenuOnMobile}>
                              <span className="icon"><Activity size={16} /></span>
                              <span className="label">Typing Test Pass Admin</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/exam-mocks" onClick={closeMenuOnMobile}>
                              <span className="icon"><Layers size={16} /></span>
                              <span className="label">Exam Mock & Category Admin</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/coupons" onClick={closeMenuOnMobile}>
                              <span className="icon"><Ticket size={16} /></span>
                              <span className="label">Coupon Management</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/courses" onClick={closeMenuOnMobile}>
                              <span className="icon"><BookOpen size={16} /></span>
                              <span className="label">Course Management</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/newsletter" onClick={closeMenuOnMobile}>
                              <span className="icon"><Mail size={16} /></span>
                              <span className="label">Newsletter Management</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/users" onClick={closeMenuOnMobile}>
                              <span className="icon"><Users size={16} /></span>
                              <span className="label">User Management</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/rate-limit" onClick={closeMenuOnMobile}>
                              <span className="icon"><Shield size={16} /></span>
                              <span className="label">Rate Limit Config</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/api-config" onClick={closeMenuOnMobile}>
                              <span className="icon"><Settings size={16} /></span>
                              <span className="label">API Configuration</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/gemini-keys" onClick={closeMenuOnMobile}>
                              <span className="icon"><Key size={16} /></span>
                              <span className="label">Gemini API Keys</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/analytics" onClick={closeMenuOnMobile}>
                              <span className="icon"><BarChart2 size={16} /></span>
                              <span className="label">Analytics Dashboard</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/feature-flags" onClick={closeMenuOnMobile}>
                              <span className="icon"><Flag size={16} /></span>
                              <span className="label">Feature Flags</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/community-config" onClick={closeMenuOnMobile}>
                              <span className="icon"><LinkIcon size={16} /></span>
                              <span className="label">Community Config</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/study-rooms" onClick={closeMenuOnMobile}>
                              <span className="icon"><Video size={16} /></span>
                              <span className="label">Study Room Management</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/bulk-email" onClick={closeMenuOnMobile}>
                              <span className="icon"><Mail size={16} /></span>
                              <span className="label">Bulk Email</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/whatsapp" onClick={closeMenuOnMobile}>
                              <span className="icon"><MessageSquare size={16} /></span>
                              <span className="label">WhatsApp Sender</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/email-queue" onClick={closeMenuOnMobile}>
                              <span className="icon"><Rocket size={16} /></span>
                              <span className="label">Email Queue</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/automation-center" onClick={closeMenuOnMobile}>
                              <span className="icon"><Bot size={16} /></span>
                              <span className="label">Automation Center</span>
                            </DropdownItem>
                            {currentUser?.roles?.includes("ROLE_SUPER_ADMIN") && (
                              <>
                                <DropdownItem to="/super-admin" onClick={closeMenuOnMobile}>
                                  <span className="icon"><Crown size={16} /></span>
                                  <span className="label">Super Admin Ops</span>
                                </DropdownItem>
                                <DropdownItem to="/admin/oracle-ssh" onClick={closeMenuOnMobile}>
                                  <span className="icon"><Terminal size={16} /></span>
                                  <span className="label">Oracle SSH Ops</span>
                                </DropdownItem>
                              </>
                            )}
                            <DropdownItem to="/admin/youtube-studio" onClick={closeMenuOnMobile}>
                              <span className="icon"><Youtube size={16} /></span>
                              <span className="label">YouTube Studio</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/subscription-warranty" onClick={closeMenuOnMobile}>
                              <span className="icon"><Shield size={16} /></span>
                              <span className="label">Warranty & expiry mail</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/corrupted-results" onClick={closeMenuOnMobile}>
                              <span className="icon"><AlertTriangle size={16} /></span>
                              <span className="label">Corrupted Test Results</span>
                            </DropdownItem>
                            <DropdownItem to="/admin/change-password" onClick={closeMenuOnMobile}>
                              <span className="icon"><Lock size={16} /></span>
                              <span className="label">Change Password</span>
                            </DropdownItem>
                          </>
                        )}
                        <DropdownDivider />
                        <DropdownItem as="a" href="/login" onClick={(e) => { logOut(); closeMenuOnMobile(); }} style={{ color: '#ff6f61' }}>
                          <span className="icon"><LogOut size={16} /></span>
                          <span className="label">Logout</span>
                        </DropdownItem>
                      </DropdownMenu>
                    </DropdownContainer>
                  </NavItem>
                </>
              ) : (
                <>
                  <NavItem>
                    <NavLink to="/login" onClick={closeMenuOnMobile}>Login</NavLink>
                  </NavItem>
                  <NavItem>
                    <SignUpLink to="/register" onClick={closeMenuOnMobile}>Sign Up</SignUpLink>
                  </NavItem>
                </>
              )}
              {!isTenantHost && (
                <NavItemFollowCluster>
                  <FollowReportCluster>
                    <Suspense fallback={null}>
                      <SocialDrawer />
                    </Suspense>
                  </FollowReportCluster>
                </NavItemFollowCluster>
              )}
            </UserMenu>
          </NavMenu>
        </Navbar>
      )}
      <Content
        isTypingDisplay={isTypingDisplay}
        isFullscreen={shouldHideNavbar}
        isHomePage={isHomePage}
      >
        <HelmetProvider>
          <Suspense fallback={<LoadingFallback />}>
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={
                isTenantHost
                  ? <Navigate to={currentUser ? (institutionOnlyUser ? "/institutional/dashboard" : "/institutional/student") : "/login"} replace />
                  : <HomeComponent />
              } />
              <Route
                path="/learn"
                element={
                  <FeatureFlagRoute featureKey="typing_lessons">
                    <LearnPage />
                  </FeatureFlagRoute>
                }
              />
              <Route
                path="/learn/lesson/:lessonId"
                element={
                  <FeatureFlagRoute featureKey="typing_lessons">
                    <LessonPlayer />
                  </FeatureFlagRoute>
                }
              />
              <Route path="/typing-display" element={<TypingDisplay />} />
              <Route path="/typing-display-test" element={<TypingDisplayTest />} />
              <Route path="/courses" element={<CoursesPage />} />
              <Route path="/institute/:slug" element={
                <Suspense fallback={<div>Loading Landing Page...</div>}>
                  <PublicLandingPage />
                </Suspense>
              } />
              <Route path="/add" element={<AddTutorial />} />
              <Route path="/courses/:courseName" element={<CourseDetails />} />
              <Route
                path="/courses/:courseName/typing-courses"
                element={<AllTypingCourses />}
              />
              <Route path="/payment" element={<PaymentComponent />} />
              <Route path="/referral" element={
                <FeatureFlagRoute featureKey="referral_system">
                  <ReferAndEarnComponent />
                </FeatureFlagRoute>
              } />
              <Route path="/home" element={
                isTenantHost
                  ? <Navigate to={currentUser ? (institutionOnlyUser ? "/institutional/dashboard" : "/institutional/student") : "/login"} replace />
                  : <HomeComponent />
              } />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/get-started" element={<PreparationStart />} />
              <Route path="/video-guides" element={<FeatureVideos />} />
              <Route path="/auth/sso/callback" element={<SSOCallback />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/start-test" element={<ChooseTest />} />
              <Route path="/typing-test/pricing" element={<TypingTestPricing />} />
              <Route path="/mod" element={<BoardModerator />} />
              <Route path="/admin" element={<BoardAdmin />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/verify-otp" element={<VerifyOtp />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/user-result" element={<TestResults />} />
              <Route path="/test-details" element={<TestDetails />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/refund-policy" element={<RefundPolicyPage />} />
              <Route path="/cookie-policy" element={<CookiePolicyPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/help" element={<HelpCenterPage />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/about" element={<About />} />
              <Route path="/for-institutes" element={<ForInstitutes />} />
              <Route path="/contact-us" element={<ContactUs />} />
              <Route path="/social" element={<SocialPage />} />
              <Route path="/leaderboard" element={
                <FeatureFlagRoute featureKey="leaderboard">
                  <LeaderboardPage />
                </FeatureFlagRoute>
              } />
              <Route path="/dsa" element={<DSAProblemDocumenter />} />
              <Route path="/blog" element={
                <FeatureFlagRoute featureKey="blog_system">
                  <BlogHub />
                </FeatureFlagRoute>
              } />
              <Route path="/typing-exam-guides" element={<ExamGuidesHub />} />
              <Route path="/all-state-typing-tests" element={<AllStateTypingTestsPage />} />
              {EXAM_LANDING_SLUGS.map((slug) => (
                <Route key={slug} path={`/${slug}`} element={<ExamLandingPage />} />
              ))}
              <Route path="/blog/:slug" element={
                <FeatureFlagRoute featureKey="blog_system">
                  <ArticlePage />
                </FeatureFlagRoute>
              } />
              <Route path="/blog-admin" element={
                <FeatureFlagRoute featureKey="blog_system">
                  <BlogAdminPanel />
                </FeatureFlagRoute>
              } />
              <Route path="/admin/feedback" element={<FeedbackAdminPanel />} />
              <Route path="/admin/typing-test-plans" element={<TypingTestPlanAdminPanel />} />
              <Route path="/admin/exam-mocks" element={<ExamMockAdminPanel />} />
              <Route path="/admin/coupons" element={<CouponAdminPanel />} />
              <Route path="/admin/courses" element={<UnifiedCourseAdminPanel />} />
              <Route path="/admin/newsletter" element={<NewsletterAdminPanel />} />
              <Route path="/admin/users" element={<UserAdminPanel />} />
              <Route path="/admin/rate-limit" element={<RateLimitAdminPanel />} />
              <Route path="/admin/api-config" element={<ApiConfigAdminPanel />} />
              <Route path="/admin/gemini-keys" element={<GeminiApiKeysAdminPanel />} />
              <Route path="/admin/analytics" element={<AnalyticsDashboard />} />
              <Route path="/admin/feature-flags" element={<FeatureFlagAdminPanel />} />
              <Route path="/admin/community-config" element={<CommunityConfigAdminPanel />} />
              <Route path="/admin/study-rooms" element={<StudyRoomAdminPanel />} />
              <Route path="/admin/bulk-email" element={<BulkEmailAdminPanel />} />
              <Route path="/admin/whatsapp" element={<WhatsAppSender />} />
              <Route path="/admin/email-queue" element={<EmailQueueDashboard />} />
              <Route path="/admin/telegram-marketing" element={<TelegramMarketingPanel />} />
              <Route path="/admin/marketing-config" element={<MarketingConfigPanel />} />
              <Route path="/admin/blog-seo" element={<BlogSeoAdminPanel />} />
              <Route path="/admin/automation-center" element={<AutomationCenterAdminPanel />} />
              <Route path="/admin/oracle-ssh" element={<OracleSshAdminPanel />} />
              <Route path="/admin/youtube-studio" element={<YouTubeStudioPanel />} />
              <Route path="/admin/subscription-warranty" element={<SubscriptionWarrantyAdminPanel />} />
              <Route path="/admin/study-subscription" element={<StudySubscriptionAdminPanel />} />
              <Route path="/admin/corrupted-results" element={<CorruptedTestResultsAdminPanel />} />
              <Route path="/admin/change-password" element={<ChangePasswordPanel />} />
              <Route path="/daily-challenge" element={
                <FeatureFlagRoute featureKey="daily_challenges">
                  <DailyChallenge />
                </FeatureFlagRoute>
              } />
              <Route path="/achievements" element={
                <FeatureFlagRoute featureKey="achievements">
                  <Achievements />
                </FeatureFlagRoute>
              } />
              <Route path="/race" element={
                <FeatureFlagRoute featureKey="typing_races">
                  <TypingRace />
                </FeatureFlagRoute>
              } />
              <Route path="/games" element={<GamesHub />} />
              <Route path="/print/mock/:paperId" element={<MockPrintView />} />
              <Route path="/games/word-blaster" element={<WordBlaster />} />
              <Route path="/games/typing-ninja" element={<TypingNinja />} />
              <Route path="/games/code-runner" element={<CodeRunner />} />
              <Route path="/games/word-tetris" element={<WordTetris />} />
              <Route path="/games/zombie-defense" element={<ZombieDefense />} />
              <Route path="/games/space-invaders" element={<SpaceInvaders />} />
              <Route path="/tournaments" element={
                <FeatureFlagRoute featureKey="tournaments">
                  <Tournaments />
                </FeatureFlagRoute>
              } />
              <Route path="/admin/tournaments" element={
                <FeatureFlagRoute featureKey="tournaments">
                  <TournamentAdmin />
                </FeatureFlagRoute>
              } />
              <Route path="/live-exams" element={
                <FeatureFlagRoute featureKey="live_exams">
                  <LiveExams />
                </FeatureFlagRoute>
              } />
              <Route path="/live-exams/:id" element={
                <FeatureFlagRoute featureKey="live_exams">
                  <LiveExamLeaderboard />
                </FeatureFlagRoute>
              } />
              
              {/* PDF Tools */}
              <Route path="/pdf-tools" element={
                <FeatureFlagRoute featureKey="pdf_tools">
                  <PdfToolsHub />
                </FeatureFlagRoute>
              } />
              <Route path="/pdf-tools/merge" element={
                <FeatureFlagRoute featureKey="pdf_tools">
                  <MergePdf />
                </FeatureFlagRoute>
              } />
              <Route path="/pdf-tools/split" element={
                <FeatureFlagRoute featureKey="pdf_tools">
                  <SplitPdf />
                </FeatureFlagRoute>
              } />
              <Route path="/pdf-tools/extract-pages" element={
                <FeatureFlagRoute featureKey="pdf_tools">
                  <ExtractPages />
                </FeatureFlagRoute>
              } />
              <Route path="/pdf-tools/remove-pages" element={
                <FeatureFlagRoute featureKey="pdf_tools">
                  <RemovePages />
                </FeatureFlagRoute>
              } />
              <Route path="/pdf-tools/rotate" element={
                <FeatureFlagRoute featureKey="pdf_tools">
                  <RotatePdf />
                </FeatureFlagRoute>
              } />
              <Route path="/pdf-tools/invert" element={
                <FeatureFlagRoute featureKey="pdf_tools">
                  <InvertPdf />
                </FeatureFlagRoute>
              } />
              <Route path="/pdf-tools/jpg-to-pdf" element={
                <FeatureFlagRoute featureKey="pdf_tools">
                  <JpgToPdf />
                </FeatureFlagRoute>
              } />
              <Route path="/pdf-tools/ai-summarizer" element={
                <FeatureFlagRoute featureKey="pdf_tools">
                  <AiSummarizer />
                </FeatureFlagRoute>
              } />
              <Route path="/pdf-tools/translate" element={
                <FeatureFlagRoute featureKey="pdf_tools">
                  <TranslatePdf />
                </FeatureFlagRoute>
              } />
              <Route path="/pdf-tools/pdf-to-markdown" element={
                <FeatureFlagRoute featureKey="pdf_tools">
                  <PdfToMarkdown />
                </FeatureFlagRoute>
              } />
              <Route path="/pdf-tools/add-watermark" element={
                <FeatureFlagRoute featureKey="pdf_tools">
                  <AddWatermark />
                </FeatureFlagRoute>
              } />
              <Route path="/pdf-tools/add-page-numbers" element={
                <FeatureFlagRoute featureKey="pdf_tools">
                  <AddPageNumbers />
                </FeatureFlagRoute>
              } />
              <Route path="/pdf-tools/pdf-to-jpg" element={
                <FeatureFlagRoute featureKey="pdf_tools">
                  <PdfToJpg />
                </FeatureFlagRoute>
              } />
              <Route path="/pdf-tools/crop" element={
                <FeatureFlagRoute featureKey="pdf_tools">
                  <CropPdf />
                </FeatureFlagRoute>
              } />
              <Route path="/pdf-tools/protect" element={
                <FeatureFlagRoute featureKey="pdf_tools">
                  <ProtectPdf />
                </FeatureFlagRoute>
              } />
              <Route path="/pdf-tools/unlock" element={
                <FeatureFlagRoute featureKey="pdf_tools">
                  <UnlockPdf />
                </FeatureFlagRoute>
              } />
              <Route path="/pdf-tools/pdf-to-word" element={
                <FeatureFlagRoute featureKey="pdf_tools">
                  <PdfToWord />
                </FeatureFlagRoute>
              } />
              <Route path="/pdf-tools/compress" element={
                <FeatureFlagRoute featureKey="pdf_tools">
                  <CompressPdf />
                </FeatureFlagRoute>
              } />
              <Route path="/pdf-tools/compare" element={
                <FeatureFlagRoute featureKey="pdf_tools">
                  <ComparePdf />
                </FeatureFlagRoute>
              } />
              <Route path="/pdf-tools/scan" element={
                <FeatureFlagRoute featureKey="pdf_tools">
                  <ScanToPdf />
                </FeatureFlagRoute>
              } />
              <Route path="/pdf-tools/:tool" element={
                <FeatureFlagRoute featureKey="pdf_tools">
                  <ToolPlaceholder />
                </FeatureFlagRoute>
              } />
              
              {/* Rank Predictor & Marks Calculator */}
              <Route path="/rank-predictor" element={
                <FeatureFlagRoute featureKey="rank_predictor">
                  <RankPredictorExamList />
                </FeatureFlagRoute>
              } />
              <Route path="/rank-predictor/:examId" element={
                <FeatureFlagRoute featureKey="rank_predictor">
                  <RankPredictorCalculator />
                </FeatureFlagRoute>
              } />
              <Route path="/rank-predictor/analyze" element={
                <FeatureFlagRoute featureKey="rank_predictor">
                  <ResultAnalyzer />
                </FeatureFlagRoute>
              } />

              <Route path="/job-updates" element={
                <FeatureFlagRoute featureKey="sarkari_results">
                  <SarkariResultsHub />
                </FeatureFlagRoute>
              } />
              <Route path="/job-updates/:id" element={
                <FeatureFlagRoute featureKey="sarkari_results">
                  <SarkariResultsDetail />
                </FeatureFlagRoute>
              } />
              <Route path="/sarkari-results" element={<Navigate to="/job-updates" replace />} />
              <Route path="/sarkari-results/:id" element={
                <FeatureFlagRoute featureKey="sarkari_results">
                  <JobResultsLegacyRedirect />
                </FeatureFlagRoute>
              } />

              {/* Steno user routes — gated by `steno`; /admin/steno is always routable (backend enforces ADMIN) */}
              <Route path="/steno" element={
                <FeatureFlagRoute featureKey="steno">
                  <StenoHome />
                </FeatureFlagRoute>
              } />
              <Route path="/steno/practice" element={
                <FeatureFlagRoute featureKey="steno">
                  <StenoPractice />
                </FeatureFlagRoute>
              } />
              <Route path="/steno/result" element={
                <FeatureFlagRoute featureKey="steno">
                  <StenoResultPage />
                </FeatureFlagRoute>
              } />
              <Route path="/steno/results" element={
                <FeatureFlagRoute featureKey="steno">
                  <StenoResults />
                </FeatureFlagRoute>
              } />
              <Route path="/steno/learn" element={
                <FeatureFlagRoute featureKey="steno">
                  <StenoLearningHub />
                </FeatureFlagRoute>
              } />
              <Route path="/steno/learn/:moduleId" element={
                <FeatureFlagRoute featureKey="steno">
                  <StenoModulePage />
                </FeatureFlagRoute>
              } />
              <Route path="/steno/learn/lesson/:lessonId" element={
                <FeatureFlagRoute featureKey="steno">
                  <StenoLessonViewer />
                </FeatureFlagRoute>
              } />
              <Route path="/steno/courses" element={<Navigate to="/courses?type=steno" replace />} />
              <Route path="/steno/pricing" element={
                <FeatureFlagRoute featureKey="steno">
                  <StenoPricing />
                </FeatureFlagRoute>
              } />
              <Route path="/steno/:examId" element={
                <FeatureFlagRoute featureKey="steno">
                  <StenoPassages />
                </FeatureFlagRoute>
              } />
              <Route path="/admin/steno" element={<StenoAdminPanel />} />

              <Route path="/admin/live-exams" element={
                <FeatureFlagRoute featureKey="live_exams">
                  <LiveExamAdmin />
                </FeatureFlagRoute>
              } />
              <Route path="/social-tasks" element={
                <FeatureFlagRoute featureKey="social_tasks">
                  <SocialTasksPage />
                </FeatureFlagRoute>
              } />
              <Route path="/friends" element={
                <FeatureFlagRoute featureKey="social_features">
                  <SocialHub />
                </FeatureFlagRoute>
              } />
              <Route path="/community" element={
                <FeatureFlagRoute featureKey="community_hub">
                  <CommunityHub />
                </FeatureFlagRoute>
              } />
              <Route path="/community/mental-health" element={
                <FeatureFlagRoute featureKey="community_hub">
                  <MentalHealthSupport />
                </FeatureFlagRoute>
              } />
              <Route path="/community/study-room" element={
                <FeatureFlagRoute featureKey="community_hub">
                  <StudyRoom />
                </FeatureFlagRoute>
              } />
              <Route path="/community/study-analytics" element={
                <FeatureFlagRoute featureKey="community_hub">
                  <StudyAnalyticsDashboard />
                </FeatureFlagRoute>
              } />
              <Route path="/community/study-buddy" element={
                <FeatureFlagRoute featureKey="community_hub">
                  <StudyBuddy />
                </FeatureFlagRoute>
              } />
              <Route path="/community/discussion-groups" element={
                <FeatureFlagRoute featureKey="community_hub">
                  <DiscussionGroups />
                </FeatureFlagRoute>
              } />
              <Route path="/community/forest-group" element={
                <FeatureFlagRoute featureKey="community_hub">
                  <ForestGroup />
                </FeatureFlagRoute>
              } />
              <Route path="/community/study-groups" element={
                <FeatureFlagRoute featureKey="community_hub">
                  <StudyGroups />
                </FeatureFlagRoute>
              } />
              <Route path="/community/yoga-meditation" element={
                <FeatureFlagRoute featureKey="community_hub">
                  <YogaMeditation />
                </FeatureFlagRoute>
              } />

              {/* Typogram Junior Routes */}
              <Route path="/junior" element={<AdventureMap />} />
              <Route path="/junior/world/:worldId" element={<WorldView />} />
              <Route path="/junior/parent-portal" element={<ParentPortal />} />
              <Route path="/junior/digital-citizenship" element={<DigitalCitizenship />} />
              <Route path="/junior/themes" element={<Themes />} />
              <Route path="/junior/games" element={<Games />} />

              {/* Typogram Institutional Routes */}
              <Route path="/institutional/student" element={<InstitutionStudentHome />} />
              <Route path="/institutional/dashboard" element={<InstitutionalDashboard />} />
              <Route path="/institutional" element={<InstitutionalDashboard />} />

              {/* Study Schedule Routes */}
              <Route path="/planner/subscription" element={
                <FeatureFlagRoute featureKey="study_schedule">
                  <StudyDashboard subscriptionPage />
                </FeatureFlagRoute>
              } />
              <Route path="/planner" element={
                <FeatureFlagRoute featureKey="study_schedule">
                  <StudyDashboard />
                </FeatureFlagRoute>
              } />
              <Route path="/study" element={
                <FeatureFlagRoute featureKey="study_schedule">
                  <StudyDashboard />
                </FeatureFlagRoute>
              } />
              <Route path="/study-schedule" element={
                <FeatureFlagRoute featureKey="study_schedule">
                  <StudyDashboard />
                </FeatureFlagRoute>
              } />

              {/* Mock Test CBT */}
              <Route path="/student/mock-test/:testId" element={
                <Suspense fallback={<div>Loading Arena...</div>}>
                  <MockTestArena />
                </Suspense>
              } />
              <Route path="/student/mock-tests" element={
                <Suspense fallback={<div>Loading Tests...</div>}>
                  <StudentMockTestList />
                </Suspense>
              } />
              <Route path="/student/mock-test/analytics/:submissionId" element={
                <Suspense fallback={<div>Loading analytics...</div>}>
                  <TestAnalyticsDashboard />
                </Suspense>
              } />
              <Route path="/student/study-library" element={
                <Suspense fallback={<div>Loading Library...</div>}>
                  <StudyLibraryHub />
                </Suspense>
              } />
              
              <Route path="/student/video-hub" element={
                <Suspense fallback={<div>Loading Video Hub...</div>}>
                  <VideoHub />
                </Suspense>
              } />
              <Route path="/student/passes" element={
                <Suspense fallback={<div>Loading Store...</div>}>
                  <TestPassStore />
                </Suspense>
              } />

              {/* System Design Simulator — toggle in Admin → Feature Flags (`system_design`) */}
              <Route path="/system-design" element={
                <FeatureFlagRoute featureKey="system_design">
                  <SystemDesignPage />
                </FeatureFlagRoute>
              } />
              <Route path="/system-design/share/:token" element={
                <FeatureFlagRoute featureKey="system_design">
                  <SystemDesignShareView />
                </FeatureFlagRoute>
              } />

              {/* Super Admin Routes */}
              <Route path="/super-admin" element={<SuperAdminDashboard />} />
              <Route path="/super-admin/dashboard" element={<SuperAdminDashboard />} />

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </HelmetProvider>
      </Content>

      {showReportIssueModal && (
        <Suspense fallback={null}>
          <FeedbackForm
            isOpen={showReportIssueModal}
            onClose={() => setShowReportIssueModal(false)}
            title={(institutionHomeLearner || isTenantHost || institutionOnlyUser) ? "Thoughts for Typogram" : "Report an issue"}
            defaultCategory="GENERAL"
          />
        </Suspense>
      )}

      {/* Telegram Join Modal (first login prompt) */}
      {showTelegramModal && (
        <Suspense fallback={null}>
          <TelegramJoinModal
            isOpen={showTelegramModal}
            onClose={() => {
              setShowTelegramModal(false);
              localStorage.setItem('telegram_prompted', 'true');
            }}
            onContinue={() => {
              setShowTelegramModal(false);
              localStorage.setItem('telegram_prompted', 'true');
            }}
          />
        </Suspense>
      )}

      {/* Cookie Consent Banner */}
      {loadCookieConsent && (
        <Suspense fallback={null}>
          <CookieConsent />
        </Suspense>
      )}

      {/* Zero-AI Standalone TypoChatbot Assistant */}
      {['/', '/study', '/steno', '/courses', '/jobs', '/about', '/contact', '/pricing'].includes(location.pathname) && (
        <Suspense fallback={null}>
          <TypoChatbot />
        </Suspense>
      )}
    </AppContainer>
  );
}

export default App;
